import React, { useState } from "react";
import { Menu, X, ShoppingBag, User, Shield } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  // Auto-expand Shop on mobile so Flower/Wax/Carts/Edibles are one tap away.
  const [shopOpen, setShopOpen] = useState(true);
  const { user, openAuth, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const navigate = useNavigate();

  const categories = [
    { key: "flower", label: "Flower" },
    { key: "wax", label: "Wax" },
    { key: "carts", label: "Carts" },
    { key: "edibles", label: "Edibles" },
  ];

  const go = (path) => {
    setMenuOpen(false);
    setShopOpen(false);
    navigate(path);
  };

  const handleLogin = () => {
    openAuth();
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="flex items-center justify-between px-5 md:px-10 h-24 md:h-28">
          <Link to="/" className="flex items-center gap-4" data-testid="brand-home-link">
            <img src={LOGO_URL} alt="High Key" className="h-16 w-16 md:h-20 md:w-20 object-contain drop-shadow-[0_0_25px_rgba(147,51,234,0.6)]" />
            <span className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white hidden sm:block">
              HIGH KEY
            </span>
          </Link>
          <div className="flex items-center gap-3 md:gap-5">
            <button
              data-testid="cart-open-btn"
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-white/80 hover:text-[var(--hk-orange)] transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={22} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[var(--hk-orange)] text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {count}
                </span>
              )}
            </button>
            {user && user.role === "owner" && (
              <Link
                to="/admin"
                data-testid="admin-nav-link"
                className="hidden md:inline-flex items-center gap-1.5 text-[var(--hk-orange)] hover:text-white text-xs font-display font-bold uppercase tracking-widest"
              >
                <Shield size={14} /> Admin
              </Link>
            )}
            {user ? (
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="hidden md:inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-white/20" />
                ) : (
                  <User size={16} />
                )}
                <span>Log out</span>
              </button>
            ) : (
              <button
                data-testid="login-btn"
                onClick={handleLogin}
                className="hidden md:inline-flex items-center gap-2 bg-white text-black px-4 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm hover:bg-gray-200 transition-colors"
              >
                <User size={14} /> Sign in
              </button>
            )}
            <button
              data-testid="hamburger-menu-btn"
              onClick={() => setMenuOpen(true)}
              className="p-2 text-white hover:text-[var(--hk-orange)] transition-colors"
              aria-label="Menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-2xl"
            data-testid="menu-drawer"
          >
            <div className="absolute inset-0 pointer-events-none opacity-30"
                 style={{ backgroundImage: "radial-gradient(circle at 70% 30%, rgba(147,51,234,0.4), transparent 60%)" }} />
            <div className="relative h-full flex flex-col px-6 md:px-16 py-8 overflow-y-auto">
              <div className="flex items-center justify-between mb-12">
                <img src={LOGO_URL} alt="High Key" className="h-20 w-20 md:h-24 md:w-24" />
                <button
                  data-testid="menu-close-btn"
                  onClick={() => setMenuOpen(false)}
                  className="p-2 text-white hover:text-[var(--hk-orange)]"
                >
                  <X size={32} />
                </button>
              </div>
              <nav className="flex-1 flex flex-col gap-2 md:gap-4">
                <motion.button
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.05 }}
                  data-testid="nav-shop-link"
                  onClick={() => setShopOpen((s) => !s)}
                  className="text-left font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white hk-underline w-fit"
                >
                  Shop <span className="text-[var(--hk-orange)]">{shopOpen ? "—" : "+"}</span>
                </motion.button>
                <AnimatePresence>
                  {shopOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden pl-4 md:pl-10 border-l-2 border-[var(--hk-purple)]"
                    >
                      <div className="py-4 flex flex-col gap-3">
                        {categories.map((c, i) => (
                          <motion.button
                            key={c.key}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: i * 0.05 }}
                            data-testid={`nav-${c.key}-link`}
                            onClick={() => go(`/shop/${c.key}`)}
                            className="text-left font-display text-2xl md:text-4xl uppercase text-[var(--hk-purple-text)] hover:text-[var(--hk-orange)] transition-colors w-fit"
                          >
                            {c.label}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <motion.button
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.15 }}
                  data-testid="nav-about-link"
                  onClick={() => go("/about")}
                  className="text-left font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white hk-underline w-fit"
                >
                  About Us
                </motion.button>
                <motion.button
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  data-testid="nav-contact-link"
                  onClick={() => go("/contact")}
                  className="text-left font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white hk-underline w-fit"
                >
                  Where to Reach Us
                </motion.button>
                {user && user.role === "owner" && (
                  <motion.button
                    initial={{ x: -40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.25 }}
                    data-testid="nav-admin-link"
                    onClick={() => go("/admin")}
                    className="text-left font-display text-3xl md:text-5xl font-bold uppercase tracking-tighter text-[var(--hk-orange)] hk-underline w-fit mt-3"
                  >
                    Admin Panel
                  </motion.button>
                )}
                <motion.button
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  data-testid={user ? "nav-logout-btn" : "nav-signin-btn"}
                  onClick={() => {
                    setMenuOpen(false);
                    setShopOpen(false);
                    if (user) {
                      logout();
                    } else {
                      handleLogin();
                    }
                  }}
                  className="text-left font-display text-3xl md:text-5xl font-bold uppercase tracking-tighter text-white hk-underline w-fit mt-3"
                >
                  {user ? "Log out" : "Sign in / Join"}
                </motion.button>
              </nav>
              <div className="flex flex-wrap gap-x-6 gap-y-2 items-center text-xs text-gray-500 font-mono-alt tracking-[0.3em] uppercase mt-8">
                <span>HIGH KEY</span>
                <span>/ EXCLUSIVE</span>
                <span>/ KOSHER</span>
                <span>/ QUALITY</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
